/**
 * SMS Templates Service
 * CRUD operations for SMS message templates
 */

import { supabase } from '../lib/supabase'

export type TemplateRecipientType = 'all' | 'customer' | 'subcontractor'

export interface SMSTemplate {
  id: string
  name: string
  content: string
  category: string | null
  recipient_type: TemplateRecipientType
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

export interface CreateSMSTemplateData {
  name: string
  content: string
  category?: string
  recipient_type?: TemplateRecipientType
  sort_order?: number
}

export interface UpdateSMSTemplateData {
  name?: string
  content?: string
  category?: string
  recipient_type?: TemplateRecipientType
  is_active?: boolean
  sort_order?: number
}

/**
 * Get all active SMS templates, optionally filtered by recipient type
 */
export async function getTemplates(recipientType?: 'customer' | 'subcontractor'): Promise<SMSTemplate[]> {
  const { data, error } = await supabase
    .from('sms_templates')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('Error fetching SMS templates:', error)
    return []
  }

  // Filter by recipient type - include 'all', null, and specific type
  if (recipientType && data) {
    return data.filter(t =>
      !t.recipient_type ||
      t.recipient_type === 'all' ||
      t.recipient_type === recipientType
    )
  }

  return data || []
}

/**
 * Get all SMS templates (including inactive)
 */
export async function getAllTemplates(): Promise<SMSTemplate[]> {
  const { data, error } = await supabase
    .from('sms_templates')
    .select('*')
    .order('sort_order', { ascending: true })

  if (error) {
    console.error('Error fetching all SMS templates:', error)
    return []
  }

  return data || []
}

/**
 * Get a single template by ID
 */
export async function getTemplateById(id: string): Promise<SMSTemplate | null> {
  const { data, error } = await supabase
    .from('sms_templates')
    .select('*')
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching SMS template:', error)
    return null
  }

  return data
}

/**
 * Create a new SMS template
 */
export async function createTemplate(template: CreateSMSTemplateData): Promise<SMSTemplate | null> {
  const { data, error } = await supabase
    .from('sms_templates')
    .insert({
      name: template.name,
      content: template.content,
      category: template.category || 'general',
      recipient_type: template.recipient_type || 'all',
      sort_order: template.sort_order || 0
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating SMS template:', error)
    return null
  }

  return data
}

/**
 * Update an existing SMS template
 */
export async function updateTemplate(id: string, updates: UpdateSMSTemplateData): Promise<SMSTemplate | null> {
  const { data, error } = await supabase
    .from('sms_templates')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single()

  if (error) {
    console.error('Error updating SMS template:', error)
    return null
  }

  return data
}

/**
 * Delete an SMS template
 */
export async function deleteTemplate(id: string): Promise<boolean> {
  const { error } = await supabase
    .from('sms_templates')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting SMS template:', error)
    return false
  }

  return true
}

/**
 * Reorder templates
 */
export async function reorderTemplates(orderedIds: string[]): Promise<boolean> {
  try {
    for (let i = 0; i < orderedIds.length; i++) {
      const { error } = await supabase
        .from('sms_templates')
        .update({ sort_order: i + 1, updated_at: new Date().toISOString() })
        .eq('id', orderedIds[i])

      if (error) {
        console.error('Error reordering template:', error)
        return false
      }
    }
    return true
  } catch (error) {
    console.error('Error in reorderTemplates:', error)
    return false
  }
}
